/**
 * Conservative URL detection.
 *
 * The old editor treated any `word.word` as a link, so German prose was
 * routinely mangled: `z.B`, `d.h`, `u.U`, `1.500` and `8.30` all silently
 * became links, with no way to undo the damage. Anything without an explicit
 * scheme or a `www.` prefix must therefore end in a known TLD to qualify.
 */

import { sanitizeUrl } from './schema';

/** Deliberately short, and weighted to this application's Austrian audience. */
const KNOWN_TLDS = [
  'at', 'de', 'com', 'org', 'net', 'eu', 'ch', 'it', 'io', 'info', 'edu', 'gov',
];

const URL_PATTERN = new RegExp(
  [
    // Explicit scheme.
    'https?://[^\\s<>"\']+',
    // Conventional web prefix.
    'www\\.[^\\s<>"\']+',
    // Email address.
    '[\\w.+-]+@[a-z0-9-]+(?:\\.[a-z0-9-]+)+',
    // Bare host, but only on a known TLD.
    `[a-z0-9][a-z0-9-]*(?:\\.[a-z0-9][a-z0-9-]*)*\\.(?:${KNOWN_TLDS.join('|')})\\b(?:/[^\\s<>"']*)?`,
  ].join('|'),
  'gi',
);

const CLOSERS: ReadonlyMap<string, string> = new Map([[')', '('], [']', '['], ['}', '{']]);

/**
 * Drops punctuation that belongs to the sentence rather than to the URL, while
 * keeping a closing bracket that the URL itself opened.
 */
function trimTrailingPunctuation(candidate: string): string {
  let result = candidate;
  while (result.length > 0) {
    const last = result[result.length - 1]!;

    if ('.,;:!?"\''.includes(last)) {
      result = result.slice(0, -1);
      continue;
    }

    const opener = CLOSERS.get(last);
    if (opener) {
      const opens = result.split(opener).length - 1;
      const closes = result.split(last).length - 1;
      if (closes > opens) {
        result = result.slice(0, -1);
        continue;
      }
    }

    break;
  }
  return result;
}

export interface UrlMatch {
  /** Index of the first character of the match within the scanned text. */
  readonly start: number;
  /** Index one past the last character. */
  readonly end: number;
  /** The absolute, scheme-validated URL to put in `href`. */
  readonly href: string;
  /** The text as the author typed it, used as the link label. */
  readonly text: string;
}

/** Finds every linkable run in a plain string. */
export function findUrls(text: string): UrlMatch[] {
  const matches: UrlMatch[] = [];
  URL_PATTERN.lastIndex = 0;

  let found = URL_PATTERN.exec(text);
  while (found) {
    const raw = trimTrailingPunctuation(found[0]);
    if (raw.length > 0) {
      const isEmail = raw.includes('@') && !/^[a-z][a-z0-9+.-]*:/i.test(raw);
      const href = sanitizeUrl(isEmail ? `mailto:${raw}` : raw);
      if (href) {
        matches.push({ start: found.index, end: found.index + raw.length, href, text: raw });
      }
    }
    found = URL_PATTERN.exec(text);
  }

  return matches;
}

/** A short, human-readable label for a URL used when a link has no text. */
export function displayUrl(href: string): string {
  try {
    const parsed = new URL(href);
    if (parsed.protocol === 'mailto:') {
      return parsed.pathname;
    }
    const shown = `${parsed.host}${parsed.pathname}`.replace(/\/$/, '');
    return shown || href;
  } catch {
    return href;
  }
}
